import * as React from "react";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import { Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { usePageViewEventTracker } from "@utils/analytics";
import { colors } from "../../styles/constants";
import { Header } from "../../components/atoms/Header";
import { FormField } from "../../components/molecules/FormField";
import { FormStack } from "../../components/molecules/FormStack";
import { SideImageTemplate } from "@templates/SideImageTemplate";
import { StripeCheckoutButton } from "../../components/atoms/StripeCheckoutButton";

const STRIPE_EXPRESS_PAYMENT_API_KEY =
  process.env["REACT_APP_STRIPE_EXPRESS_PAYMENT_API_KEY"] || "price_1N8LNFJltnCeXB4sHAY7W8Fd";

// Define constant for items in the table
const tableItems = [
  "Request account closure, freeze or transfer using this form",
  "Request cancellation of direct debits",
  "Receive Date of Death balances (required for Probate)",
  "Receive closure forms for banks",
  "Request marketing and mail to be stopped",
];

const getTable = () => (
  <table style={{ width: "100%", height: "100%" }}>
    <tbody>
      <tr style={{ padding: "10px 0", textAlign: "center" }}>
        <td></td>

        <td colSpan={2} style={{ fontSize: "30px" }}>
          <b>£9.99</b>
        </td>
      </tr>
      <tr>
        <td></td>

        <td colSpan={2} style={{ fontSize: "14px", padding: "10px 0", textAlign: "center" }}>
          &nbsp; Manage accounts for banks, insurers, utilities, subscriptions and all other
          companies.
        </td>
      </tr>
      {tableItems.map((item) => (
        <React.Fragment key={item}>
          <tr style={{ height: "50px" }}>
            <td></td>
            <td>✅</td>
            <td>{item}</td>
          </tr>
        </React.Fragment>
      ))}
    </tbody>
  </table>
);

interface CompanyNotificationMessageProps {
  companyName: string;
}

const CompanyNotificationMessage: React.FC<CompanyNotificationMessageProps> = ({ companyName }) => (
  <Stack columnGap={1} direction="row">
    <CheckCircleOutlineOutlinedIcon fontSize="small" sx={{ color: colors.lightTeal }} />
    <b>{companyName} successfully notified</b>
  </Stack>
);

interface ExpressPaymentPageViewProps {
  serviceProvider: { companyName: string };
  successUrl: string;
  paymentFailureUrl: string;
}

export const ExpressPaymentPageView: React.FC<ExpressPaymentPageViewProps> = ({
  serviceProvider,
  successUrl,
  paymentFailureUrl,
}) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <SideImageTemplate headerType="help" image={getTable()}>
      <Stack rowGap={4}>
        {matches ? (
          <>
            <Header level={1} color={colors.accentTeal}>
              {serviceProvider.companyName} successfully notified
            </Header>
            <Header level={2}>Would you like to notify more companies?</Header>
          </>
        ) : (
          <>
            <Typography variant="body1" color={colors.accentTeal}>
              <CompanyNotificationMessage companyName={serviceProvider.companyName} />
            </Typography>
            <Header level={1} color={colors.bodyText}>
              Would you like to notify more companies?
            </Header>
          </>
        )}
        <Typography variant="body1">
          <b>
            {" "}
            Use the information you have just provided to report the death to banks, insurers,
            utilities and many more companies - in one go.{" "}
          </b>
          Simply add the companies you want to contact (
          <a target="_blank" href={"https://www.settld.care/companies-we-notify/"} rel="noreferrer">
            {" "}
            here’s who we work with{" "}
          </a>
          ) and tell us what you want to happen to each account (closure, transfer, freeze). Account
          updates and information will then appear in one secure place, for you to review at any
          time.
        </Typography>
        <FormStack>
          <FormField twoThirdsWidth>
            <StripeCheckoutButton
              label="Proceed With Payment"
              priceId={STRIPE_EXPRESS_PAYMENT_API_KEY}
              successUrl={successUrl}
              paymentFailureUrl={paymentFailureUrl}
            />
          </FormField>
        </FormStack>
      </Stack>
    </SideImageTemplate>
  );
};

export const ExpressPaymentPage: React.FC<ExpressPaymentPageViewProps> = ({
  serviceProvider,
  successUrl,
  paymentFailureUrl,
}) => {
  usePageViewEventTracker("Express form Payment Initiated");

  return (
    <ExpressPaymentPageView
      serviceProvider={serviceProvider}
      successUrl={successUrl}
      paymentFailureUrl={paymentFailureUrl}
    />
  );
};
